/**
 * DATPdfScanModal — Upload a DAT-Kalkulations-PDF, parse via Gemini Vision,
 * preview extracted fields and let the user merge them into the case.
 *
 * Used as a fallback when the live DAT-API integration is unavailable, OR
 * triggered automatically from DocumentsStep when an uploaded PDF is
 * detected as a DAT-Kalkulation.
 */
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, FileText, Upload, AlertCircle, CheckCircle2, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmtEUR = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};

const FIELD_GROUPS = [
  { key: "vehicle", label: "Fahrzeugdaten", icon: FileText },
  { key: "calculation", label: "Kostenzusammenfassung", icon: FileText },
  { key: "positions", label: "Detail-Positionen (Ersatzteile, Arbeitslohn, Lackierung)", icon: FileText },
];

export default function DATPdfScanModal({ caseId, open, onClose, onApplied, initialFile = null }) {
  const [file, setFile] = useState(initialFile);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({ vehicle: true, insurance: true, calculation: true, positions: true });
  const [applying, setApplying] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setFile(null); setResult(null); setError(""); setScanning(false);
  };

  const runScan = async (f) => {
    if (!f) return;
    setScanning(true); setError(""); setResult(null);
    try {
      // ---- 1) Job anlegen (sofortige Response, kein Ingress-Timeout) ----
      // Upload-Timeout: 90 s, damit grosse PDFs auf langsamen mobilen
      // Verbindungen ankommen können (20 MB hard cap im Backend).
      const fd = new FormData();
      fd.append("file", f);
      const { data: job } = await api.post("/ai/scan-dat-pdf/jobs", fd, { timeout: 90_000 });
      // Fast-Path: deterministischer Server-Parser hat sofort geliefert —
      // kein Polling nötig, Ergebnis direkt anzeigen.
      if (job?.status === "completed" && job?.result) {
        setResult(job.result);
        return;
      }
      const jobId = job?.job_id;
      if (!jobId) {
        setError("Konnte den Scan-Job nicht starten.");
        return;
      }

      // ---- 2) Polling: alle 2 s den Status abfragen, bis fertig ----
      // Hard-Cutoff bei 4 min (KI-Calls dauern selten länger als 90 s).
      const maxAttempts = 120; // 120 × 2 s = 4 min
      let finalJob = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const { data } = await api.get(`/ai/scan-dat-pdf/jobs/${jobId}`);
          if (data?.status === "completed" || data?.status === "failed") {
            finalJob = data;
            break;
          }
        } catch (pollErr) {
          // Polling-Fehler (z.B. 404 wenn Job abgelaufen) — abbrechen
          finalJob = { status: "failed", error: apiError(pollErr, "Status-Abfrage fehlgeschlagen") };
          break;
        }
      }
      if (!finalJob) {
        setError("Der Scan dauert ungewöhnlich lange — bitte später erneut versuchen.");
        return;
      }
      if (finalJob.status === "failed") {
        setError(finalJob.error || "Scan fehlgeschlagen.");
        return;
      }
      const data = finalJob.result;
      if (data?.error) {
        setError(data.notes || data.error || "Konnte keine strukturierten Daten extrahieren.");
      } else {
        setResult(data);
      }
    } catch (e) {
      // Specific hints depending on the error class
      const status = e?.response?.status;
      let msg = apiError(e, "Scan fehlgeschlagen");
      if (status === 413) {
        msg = "PDF ist zu groß (max. 20 MB). Bitte komprimieren oder Seiten reduzieren.";
      } else if (status === 502 || status === 504) {
        // Sollte mit Async-Pattern eigentlich nicht mehr passieren — nur
        // wenn schon der Job-Anlage-POST blockt (sehr unwahrscheinlich).
        msg = "Server kurzfristig nicht erreichbar — bitte erneut versuchen.";
      }
      setError(msg);
    } finally {
      setScanning(false);
    }
  };

  const onPickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf") && f.type !== "application/pdf") {
      toast.error("Bitte ein PDF auswählen");
      return;
    }
    setFile(f);
    runScan(f);
  };

  // ------------------------------------------------------------------
  // Sanitizer — Claude liefert manchmal `null` für leere Strings oder
  // gibt `equipment` als Array zurück. Das Pydantic-Backend-Modell
  // erwartet aber strikt `str = ""` für String-Felder. Ohne diese
  // Säuberung wirft die PATCH-Validierung "Input should be a valid
  // string" zurück. Wir konvertieren defensiv:
  //   • null/undefined → Feld wird übersprungen (kein Überschreiben)
  //   • Array<string> → "\n".join für equipment-Feld
  //   • "50.000 km" → 50000 für integer-Felder
  // Schlüssel, die das Modell nicht kennt, werden gefiltert
  // (sonst landen Roh-Hilfsfelder wie sub_model in Mongo).
  // Field-Mapping berücksichtigt Unterschiede zwischen Claude-Output
  // und Backend-Modell (z.B. `dat_europa_code` → `dat_eur_code`).
  // ------------------------------------------------------------------
  const VEHICLE_STR_FIELDS = [
    "vin", "license_plate", "make", "model", "color", "fuel_type",
    "hsn", "tsn", "dat_eur_code", "container", "equipment", "tires",
    "vehicle_type", "mileage_source", "first_registration", "next_hu",
  ];
  const VEHICLE_INT_FIELDS = ["year", "mileage", "power_kw", "construction_time"];
  // Manche Claude-Felder heißen anders als im Pydantic-Modell — wir
  // ziehen Aliase entsprechend rüber.
  const VEHICLE_FIELD_ALIASES = {
    dat_europa_code: "dat_eur_code",
    europa_code: "dat_eur_code",
  };
  const INSURANCE_STR_FIELDS = [
    "insurer_name", "insurer_address", "insurer_phone", "insurer_email",
    "policy_number", "claim_number", "coverage_type", "damage_location",
    "claim_date",
  ];
  const CALCULATION_NUMERIC_FIELDS = [
    "labor_net", "paint_net", "parts_net", "surcharge_net",
    "subtotal_net", "tax_amount", "tax_rate", "total_gross",
  ];

  const toCleanInt = (v) => {
    if (v === null || v === undefined || v === "") return null;
    if (typeof v === "number") return Number.isFinite(v) ? Math.round(v) : null;
    const s = String(v).replace(/[^\d-]/g, "");
    if (!s) return null;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  };

  const sanitizeVehicle = (raw) => {
    if (!raw || typeof raw !== "object") return null;
    // Aliase auflösen — schreibe `dat_europa_code` als `dat_eur_code`.
    const aliased = { ...raw };
    for (const [from, to] of Object.entries(VEHICLE_FIELD_ALIASES)) {
      if (aliased[from] !== undefined && aliased[to] === undefined) {
        aliased[to] = aliased[from];
      }
    }
    const out = {};
    for (const key of VEHICLE_STR_FIELDS) {
      const v = aliased[key];
      if (v === null || v === undefined) {
        // leere Werte einfach skippen — sonst überschreiben wir
        // bestehende Felder in alten Akten mit Leer-Strings.
        continue;
      }
      if (Array.isArray(v)) {
        out[key] = v.filter(Boolean).map(String).join("\n");
      } else {
        const s = String(v).trim();
        if (s) out[key] = s;
      }
    }
    for (const key of VEHICLE_INT_FIELDS) {
      const n = toCleanInt(aliased[key]);
      if (n !== null) out[key] = n;
    }
    return Object.keys(out).length > 0 ? out : null;
  };

  const sanitizeInsurance = (raw) => {
    if (!raw || typeof raw !== "object") return null;
    const out = {};
    for (const key of INSURANCE_STR_FIELDS) {
      const v = raw[key];
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (s) out[key] = s;
    }
    return Object.keys(out).length > 0 ? out : null;
  };

  const sanitizeCalculation = (raw) => {
    if (!raw || typeof raw !== "object") return null;
    const summary = {};
    for (const key of CALCULATION_NUMERIC_FIELDS) {
      const v = raw[key];
      if (v === null || v === undefined) continue;
      const num = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
      if (Number.isFinite(num)) summary[key] = num;
    }
    return Object.keys(summary).length > 0 ? summary : null;
  };

  // Detail-Positionen ins DAT-Pull-Back-Format mappen, damit die
  // Dokumentation (die diese Felder bereits anzeigt) sie direkt nutzt.
  // Claude liefert `positions.parts` + `positions.labor` + `positions.paint`,
  // das Backend speichert `calculation.spare_parts` + `calculation.labour_positions`.
  const sanitizePositions = (raw) => {
    if (!raw || typeof raw !== "object") return { spare_parts: null, labour_positions: null };
    const cleanList = (arr) => {
      if (!Array.isArray(arr)) return null;
      const cleaned = arr.filter((it) => it && typeof it === "object").map((it) => {
        const copy = {};
        for (const [k, v] of Object.entries(it)) {
          if (v === null || v === undefined) continue;
          copy[k] = v;
        }
        return copy;
      }).filter((it) => Object.keys(it).length > 0);
      return cleaned.length > 0 ? cleaned : null;
    };
    // `paint` an die Lohn-Positionen anhängen (mit Marker), damit nichts
    // verloren geht. DAT trennt das nicht strikt, aber für die Dokumentation
    // ist ein Lohnposten ein Lohnposten.
    const labour = cleanList(raw.labor) || [];
    const paint = cleanList(raw.paint) || [];
    for (const p of paint) {
      labour.push({ ...p, position_type: "paint" });
    }
    return {
      spare_parts: cleanList(raw.parts),
      labour_positions: labour.length > 0 ? labour : null,
    };
  };

  const apply = async () => {
    if (!result || !caseId) return;
    setApplying(true);
    try {
      const patch = {};
      if (selected.vehicle && result.vehicle) {
        const cleaned = sanitizeVehicle(result.vehicle);
        if (cleaned) patch.vehicle = cleaned;
      }
      if (selected.insurance && result.insurance) {
        const cleaned = sanitizeInsurance(result.insurance);
        if (cleaned) patch.insurance = cleaned;
      }
      // Calculation: Summen + (wenn vorhanden) Vorgangsnummer
      const calcPart = {};
      if (selected.calculation && result.calculation) {
        const summary = sanitizeCalculation(result.calculation);
        if (summary) {
          calcPart.summary = {
            labor_net: summary.labor_net || 0,
            paint_net: summary.paint_net || 0,
            parts_net: summary.parts_net || 0,
            surcharge_net: summary.surcharge_net || 0,
            subtotal_net: summary.subtotal_net || 0,
            tax_amount: summary.tax_amount || 0,
            tax_rate: summary.tax_rate || 19,
            total_gross: summary.total_gross || 0,
          };
        }
        // DAT-Vorgangsnummer extrahieren (z.B. "122575735") und auf
        // Akten-Top-Level setzen — danach funktioniert "Aus DAT abrufen"
        // automatisch ohne dass der User die Nummer manuell eintippt.
        const datRefRaw = result.calculation.dat_reference;
        const datRef = datRefRaw ? String(datRefRaw).replace(/[^\d]/g, "") : "";
        if (datRef && /^\d{6,}$/.test(datRef)) {
          patch.dat_contract_id = parseInt(datRef, 10);
        }
      }
      if (selected.positions && result.positions) {
        const { spare_parts, labour_positions } = sanitizePositions(result.positions);
        if (spare_parts) calcPart.spare_parts = spare_parts;
        if (labour_positions) calcPart.labour_positions = labour_positions;
      }
      if (Object.keys(calcPart).length > 0) {
        patch.calculation = calcPart;
      }
      if (Object.keys(patch).length === 0) {
        toast.info("Keine Daten ausgewählt — nichts zu übernehmen.");
        return;
      }
      await api.patch(`/cases/${caseId}`, patch);

      // ─────────────────────────────────────────────────────────────────
      // Original-PDF als Akten-Dokument anhängen.
      // So bleibt die DAT-Kalkulation revisionssicher in der Dokumentation,
      // unabhängig vom KI-Extrakt. User kann sie später erneut öffnen oder
      // den Versicherern beilegen. Schlägt das fehl (z.B. Object-Storage
      // kurz nicht erreichbar), brechen wir den Scan-Apply nicht ab — die
      // strukturierten Daten sind ja schon in der Akte.
      // ─────────────────────────────────────────────────────────────────
      if (file) {
        try {
          const docFd = new FormData();
          docFd.append("files", file, file.name || "DAT-Kalkulation.pdf");
          docFd.append("doc_type", "dat_calculation");
          await api.post(`/files/cases/${caseId}/documents`, docFd, { timeout: 30_000 });
        } catch (uploadErr) {
          // Nur leise warnen — die strukturierten Daten sind übernommen.
          console.warn("PDF-Anhang fehlgeschlagen:", uploadErr);
          toast.warning("Daten übernommen, aber PDF-Anhang fehlgeschlagen — bitte manuell hochladen.");
        }
      }

      // Erfolgsmeldung mit konkreter Übersicht was übernommen wurde
      const summary = [];
      if (patch.vehicle) summary.push(`${Object.keys(patch.vehicle).length} Fahrzeugfelder`);
      if (patch.insurance) summary.push(`${Object.keys(patch.insurance).length} Versicherungsfelder`);
      if (patch.calculation?.summary) summary.push("Kalkulations-Summen");
      if (patch.calculation?.spare_parts) summary.push(`${patch.calculation.spare_parts.length} Ersatzteile`);
      if (patch.calculation?.labour_positions) summary.push(`${patch.calculation.labour_positions.length} Lohn-Positionen`);
      if (patch.dat_contract_id) summary.push(`DAT-Vorgang #${patch.dat_contract_id}`);
      if (file) summary.push("PDF-Anhang");
      toast.success(
        summary.length > 0
          ? `Übernommen: ${summary.join(" · ")}`
          : "Daten in die Akte übernommen"
      );
      onApplied?.(patch);
      reset();
      onClose?.();
    } catch (e) {
      toast.error(apiError(e, "Übernahme fehlgeschlagen"));
    } finally {
      setApplying(false);
    }
  };

  // Auto-scan when modal opens with an initialFile
  React.useEffect(() => {
    if (open && initialFile && !result && !scanning) {
      setFile(initialFile);
      runScan(initialFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFile]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dat-pdf-scan-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-600" /> DAT-Kalkulations-PDF scannen
          </DialogTitle>
          <DialogDescription>
            Lade ein DAT-Schadenkalkulations-PDF hoch — die KI extrahiert Fahrzeugdaten, Kosten und alle Detail-Positionen
            und überträgt sie auf Wunsch direkt in die Akte.
          </DialogDescription>
        </DialogHeader>

        {/* Upload zone */}
        {!file && (
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              onPickFile(e.dataTransfer?.files?.[0]);
            }}
            className={`flex flex-col items-center justify-center gap-3 py-12 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
              dragOver ? "border-cyan-500 bg-cyan-50/60" : "border-zinc-200 hover:border-cyan-400 hover:bg-cyan-50/30"
            }`}
            data-testid="dat-pdf-dropzone"
          >
            <Upload className="h-10 w-10 text-zinc-400" strokeWidth={1.4} />
            <div className="text-sm font-medium text-zinc-700">PDF hier ablegen oder klicken</div>
            <div className="text-[11px] text-zinc-500">DAT-Schadenkalkulation · max. 20 MB</div>
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])}
              data-testid="dat-pdf-file-input"
            />
          </label>
        )}

        {/* Scanning state */}
        {scanning && (
          <div className="py-12 text-center">
            <Loader2 className="h-10 w-10 mx-auto text-cyan-600 animate-spin" />
            <div className="mt-4 text-sm font-medium text-zinc-900">PDF wird analysiert …</div>
            <div className="mt-1 text-xs text-zinc-500">Gemini extrahiert Fahrzeugdaten und Positionen — kann 20–60 Sek. dauern</div>
            <div className="mt-2 text-[11px] text-zinc-400">{file?.name}</div>
          </div>
        )}

        {/* Error */}
        {error && !scanning && (
          <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl" data-testid="dat-pdf-scan-error">
            <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-rose-900">Scan nicht erfolgreich</div>
              <div className="text-sm text-rose-700 mt-1">{error}</div>
              <Button variant="outline" size="sm" className="mt-3" onClick={reset} data-testid="dat-pdf-retry-btn">
                Andere Datei wählen
              </Button>
            </div>
          </div>
        )}

        {/* Result preview */}
        {result && !scanning && (
          <div className="space-y-4" data-testid="dat-pdf-scan-result">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <div className="text-sm">
                <span className="font-medium text-emerald-900">PDF erfolgreich gescannt</span>
                {result.confidence && (
                  <Badge variant="outline" className="ml-2 bg-white border-emerald-300 text-emerald-700 capitalize text-[10px]">
                    Konfidenz: {result.confidence}
                  </Badge>
                )}
              </div>
            </div>

            {/* Vehicle group */}
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <label className="flex items-center gap-2 p-3 bg-zinc-50 border-b border-zinc-200 cursor-pointer">
                <Checkbox
                  checked={selected.vehicle}
                  onCheckedChange={(v) => setSelected((s) => ({ ...s, vehicle: !!v }))}
                  data-testid="dat-pdf-select-vehicle"
                />
                <span className="font-semibold text-sm">Fahrzeugdaten übernehmen</span>
              </label>
              <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                {[
                  ["VIN", result.vehicle?.vin],
                  ["HSN / TSN", result.vehicle?.hsn && result.vehicle?.tsn ? `${result.vehicle.hsn} / ${result.vehicle.tsn}` : null],
                  ["Kennzeichen", result.vehicle?.license_plate],
                  ["Hersteller", result.vehicle?.make],
                  ["Modell", result.vehicle?.model],
                  ["Untertyp", result.vehicle?.sub_model],
                  ["Erstzulassung", result.vehicle?.first_registration],
                  ["Laufleistung", result.vehicle?.mileage ? `${result.vehicle.mileage.toLocaleString("de-DE")} km` : null],
                  ["Leistung", result.vehicle?.power_kw ? `${result.vehicle.power_kw} kW` : null],
                  ["Hubraum", result.vehicle?.displacement_ccm ? `${result.vehicle.displacement_ccm} ccm` : null],
                  ["Kraftstoff", result.vehicle?.fuel_type],
                  ["Getriebe", result.vehicle?.gearbox],
                  ["Farbe", result.vehicle?.color],
                ].filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                  <React.Fragment key={k}>
                    <div className="text-zinc-500">{k}</div>
                    <div className="font-medium text-zinc-900">{v}</div>
                  </React.Fragment>
                ))}
                {result.vehicle?.equipment?.length > 0 && (
                  <>
                    <div className="text-zinc-500 col-span-2 mt-2 font-semibold text-[11px] uppercase tracking-wider">Ausstattung</div>
                    <div className="col-span-2 flex flex-wrap gap-1">
                      {result.vehicle.equipment.map((eq, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{eq}</Badge>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Insurance group — nur anzeigen, wenn das PDF Versicherungsdaten enthält */}
            {result.insurance && Object.values(result.insurance).some((v) => v && String(v).trim()) && (
              <div className="border border-zinc-200 rounded-xl overflow-hidden" data-testid="dat-pdf-insurance">
                <label className="flex items-center gap-2 p-3 bg-zinc-50 border-b border-zinc-200 cursor-pointer">
                  <Checkbox
                    checked={selected.insurance}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, insurance: !!v }))}
                    data-testid="dat-pdf-select-insurance"
                  />
                  <span className="font-semibold text-sm">Versicherungsdaten übernehmen</span>
                </label>
                <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                  {[
                    ["Versicherer", result.insurance?.insurer_name],
                    ["Schadennummer", result.insurance?.claim_number],
                    ["Schadentag", result.insurance?.claim_date],
                    ["Versicherungsnehmer", result.insurance?.policyholder],
                    ["Deckung", result.insurance?.coverage_type],
                  ].filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                    <React.Fragment key={k}>
                      <div className="text-zinc-500">{k}</div>
                      <div className="font-medium text-zinc-900">{v}</div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}


            {/* Calculation group */}
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <label className="flex items-center gap-2 p-3 bg-zinc-50 border-b border-zinc-200 cursor-pointer">
                <Checkbox
                  checked={selected.calculation}
                  onCheckedChange={(v) => setSelected((s) => ({ ...s, calculation: !!v }))}
                  data-testid="dat-pdf-select-calculation"
                />
                <span className="font-semibold text-sm">Kostenzusammenfassung übernehmen</span>
              </label>
              <table className="w-full text-[12px]">
                <tbody>
                  {[
                    ["Arbeitslohn (netto)", result.calculation?.labor_net],
                    ["Lackierung (netto)", result.calculation?.paint_net],
                    ["Ersatzteile (netto)", result.calculation?.parts_net],
                    ["Nebenkosten (netto)", result.calculation?.surcharge_net],
                    ["Summe (netto)", result.calculation?.subtotal_net],
                    [`${result.calculation?.tax_rate ? result.calculation.tax_rate + "%" : "19%"} MwSt.`, result.calculation?.tax_amount],
                    ["Summe (brutto)", result.calculation?.total_gross, true],
                  ].map(([k, v, accent], i) => (
                    <tr key={i} className={accent ? "bg-cyan-50 font-bold text-cyan-900" : ""}>
                      <td className="px-3 py-1.5 text-zinc-600">{k}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtEUR(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Positions group */}
            {(result.positions?.parts?.length > 0 || result.positions?.labor?.length > 0) && (
              <div className="border border-zinc-200 rounded-xl overflow-hidden">
                <label className="flex items-center gap-2 p-3 bg-zinc-50 border-b border-zinc-200 cursor-pointer">
                  <Checkbox
                    checked={selected.positions}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, positions: !!v }))}
                    data-testid="dat-pdf-select-positions"
                  />
                  <span className="font-semibold text-sm">Detail-Positionen übernehmen</span>
                  <Badge variant="outline" className="ml-1 text-[10px]">
                    {(result.positions?.parts?.length || 0) + (result.positions?.labor?.length || 0) + (result.positions?.paint?.length || 0)} Zeilen
                  </Badge>
                </label>
                <div className="p-3 text-[11px] text-zinc-600">
                  <div>Ersatzteile: <b>{result.positions?.parts?.length || 0}</b> · Arbeitslohn: <b>{result.positions?.labor?.length || 0}</b> · Lackierung: <b>{result.positions?.paint?.length || 0}</b></div>
                </div>
              </div>
            )}

            {result.notes && (
              <div className="text-[11px] text-zinc-500 italic">Hinweis: {result.notes}</div>
            )}
          </div>
        )}

        <DialogFooter>
          {result && !scanning && (
            <>
              <Button variant="outline" onClick={reset} data-testid="dat-pdf-cancel-btn">
                Andere Datei
              </Button>
              <Button
                onClick={apply}
                disabled={applying || (!selected.vehicle && !selected.calculation && !selected.positions)}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="dat-pdf-apply-btn"
              >
                {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Daten in Akte übernehmen
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
